import type {
  ColumnMappings,
  FinalMappedData,
  Subsidiary,
} from '@revelio/core';

import { environment } from '../../../environments/environment';
import {
  fetchSubsidiaries,
  transformMappingData,
} from '../../deliverables/company-selection/subsidiary-mapping/subsidiary-mapping.api';
import type { DataBuilderCompanySelections } from '../data-builder.store';
import type {
  CompanyMapperSelectionType,
  DataBuilderCompanyMapper,
} from './data-builder-api.types';
import { fetchJson } from './fetch-util';

const CREATE_REF_TABLES_URL = `${environment.COMPANY_MAPPER_SNOWFLAKE_URL}/create-ref-tables`;

type CreateRefTablesRequest = {
  name: string;
  rcid: number;
  child_rcids: number[];
  expand_rcids: number[];
  company_id: string;
};

type CreateRefTablesResponse = {
  input_table_name: string;
  ref_table_name: string;
};

const toCompanyMapper = (
  response: CreateRefTablesResponse,
  selectionType: CompanyMapperSelectionType
): DataBuilderCompanyMapper => ({
  type: 'company_mapper',
  pipeline_input: response.input_table_name,
  company_reference: response.ref_table_name,
  selection_type: selectionType,
});

const normalizeCreateRefTablesRequest = (
  payload: FinalMappedData[] | CreateRefTablesRequest[]
): CreateRefTablesRequest[] =>
  payload.map((item) => ({
    name: String(item.name),
    rcid: item.rcid,
    child_rcids: item.child_rcids,
    expand_rcids: item.expand_rcids,
    company_id: item.company_id,
  }));

const createCompanyMapper = async (
  payload: FinalMappedData[] | CreateRefTablesRequest[],
  selectionType: CompanyMapperSelectionType
): Promise<DataBuilderCompanyMapper | null> => {
  if (payload.length === 0) {
    return null;
  }

  const response = await fetchJson<CreateRefTablesResponse>(
    CREATE_REF_TABLES_URL,
    {
      method: 'POST',
      body: JSON.stringify(normalizeCreateRefTablesRequest(payload)),
    }
  );

  return toCompanyMapper(response, selectionType);
};

// CompanyRefInfo from the SelectionLists query carries no has_subsidiaries flag,
// so populateCompanyMap in company-search-tree.tsx hardcodes false for tree picks.
// Verify against /subsidiaries so tree-picked parents aren't treated as leaves;
// also self-corrects any /search response that under-reports.
export async function enrichSubsidiaryList(
  companies: Subsidiary[]
): Promise<Subsidiary[]> {
  return Promise.all(
    companies.map(async (company) => {
      if (company.hasSubsidiaries) return company;
      const rcid = Number(company.rcid);
      if (Number.isNaN(rcid)) return company;
      try {
        const subs = await fetchSubsidiaries(rcid);
        if (subs.length === 0) return company;
        return { ...company, hasSubsidiaries: true };
      } catch {
        return company;
      }
    })
  );
}

async function enrichHasSubsidiaries(
  selections: DataBuilderCompanySelections
): Promise<DataBuilderCompanySelections> {
  const entries = await Promise.all(
    Object.entries(selections).map(async ([rcidKey, value]) => {
      if (value.hasSubsidiaries) return [rcidKey, value] as const;
      const rcid = Number(rcidKey);
      if (Number.isNaN(rcid)) return [rcidKey, value] as const;
      try {
        const subs = await fetchSubsidiaries(rcid);
        if (subs.length === 0) return [rcidKey, value] as const;
        return [rcidKey, { ...value, hasSubsidiaries: true }] as const;
      } catch {
        return [rcidKey, value] as const;
      }
    })
  );
  return Object.fromEntries(entries);
}

function toCreateRefTablesRequest(
  selections: DataBuilderCompanySelections
): CreateRefTablesRequest[] {
  const out: CreateRefTablesRequest[] = [];
  for (const [rcidKey, value] of Object.entries(selections)) {
    const rcid = Number(rcidKey);
    if (Number.isNaN(rcid)) {
      continue;
    }
    const title = value.title ?? value.label ?? rcidKey;
    const companyId =
      value.company_id !== undefined && value.company_id !== null
        ? String(value.company_id)
        : rcidKey;
    out.push({
      name: title,
      rcid,
      child_rcids: value.hasSubsidiaries ? [] : [rcid],
      expand_rcids: value.hasSubsidiaries ? [rcid] : [],
      company_id: companyId,
    });
  }
  return out;
}

export async function createCompanyMapperFromMappings(
  mappings: ColumnMappings,
  selectionType: CompanyMapperSelectionType
): Promise<DataBuilderCompanyMapper | null> {
  const payload = transformMappingData(mappings);
  return createCompanyMapper(payload, selectionType);
}

export async function createCompanyMapperFromSelections(
  selections: DataBuilderCompanySelections,
  selectionType: CompanyMapperSelectionType
): Promise<DataBuilderCompanyMapper | null> {
  const enriched = await enrichHasSubsidiaries(selections);
  const payload = toCreateRefTablesRequest(enriched);
  return createCompanyMapper(payload, selectionType);
}
